import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import cookies from 'js-cookie';

import { cookieNames, useStepper } from '@monorepo/common';

import { Step1 } from './steps/Step1';
import { Step2 } from './steps/Step2';
import { Step3 } from './steps/Step3';
import { Step4 } from './steps/Step4';
import { Step5 } from './steps/Step5';
import { Step6 } from './steps/Step6';
import { Step7 } from './steps/Step7';

import { IStep1Form } from './steps/Step1/formInterface';
import { IStep2Form } from './steps/Step2/formInterface';
import { IStep3Form } from './steps/Step3/formInterfaces';
import { IStep4Form } from './steps/Step4/formInterface';
import { IStep5Form } from './steps/Step5/formInterface';
import { IStep6Form } from './steps/Step6/formInterfaces';
import { IStep7Form } from './steps/Step7/formInterfaces';

import { showNotification } from '../../features/sliceNotification';
import { useAppSelector } from '../../store/hooks';
import {
  useGetJobTempDataQuery,
  useSaveStepsDataMutation,
  usePaymentRequestMutation,
  useFreePaymentRequestMutation,
  useGetPaymentIntentMutation,
  useBenefitCheckoutMutation,
  useReleaseBenefitCheckoutMutation
} from '../../services/servicePostAJob';
import { useGetUserInfoQuery } from '../../services/serviceUserInfo';
import {
  staticValuesForStep1,
  makeStaticValuesForStep1,
  makeStaticValuesForStep2,
  makeStaticValueForStep3,
  makeStaticValuesForStep4,
  makeStaticValuesForStep5,
  makeStaticValuesForStep6,
  makeStaticValuesForStep7
} from './staticValues';

import { showDropButton } from '../../features/sliceStepper';
import {
  IJobBenefitClaim,
  IJobBenefitSelection,
  JobListingTypesResponse
} from '../../services/servicePostAJob/responseInterfaces';
import { buildPostAJobIntentBody, interpretIntentResponse, GENERIC_INTENT_ERROR } from '../JobPayment';
import { buildBenefitCheckoutBody } from '../JobPayment/benefitBodies';
import {
  IHeldBenefitClaim,
  clearHeldBenefitClaim,
  readHeldBenefitClaim,
  storeHeldBenefitClaim,
} from '../../utils/benefitClaim';
import { readUtmCookie } from '../../utils/utm';
import {
  parseServerStepErrors,
  getResponseErrors,
  getResponseDomainError,
  GENERIC_SERVER_ERROR
} from '../../utils/serverStepErrors';
import { dropStepSlice, StepServerErrors } from './dropStepSlice';
import { LeavePageGuard, UNPOSTED_LISTING_MESSAGE } from '../LeavePageGuard';

// Which UI step owns each server validation key, for THIS flow only. Post a Job is
// the one flow where StepN maps to index N-1; Reactivate's `Step2` DTO is actually
// PostJobStep7 at index 1, and Prepaid Purchase spreads Step1/Step2 over three UI
// steps — so this must never be lifted into a shared constant.
// Only steps with a react-hook-form instance are listed: Step1 and Step5 have no
// form and Step7 is Stripe Elements, so their keys fall through to the notification.
const POST_JOB_STEP_INDEX: Record<string, number> = {
  Step2: 1,
  Step3: 2,
  Step4: 3,
  Step6: 5
};

// Shown when the server rejected fields we have navigated to; the messages
// themselves are now on the fields, so the toast just points at them.
const STEP_ERRORS_NOTICE = 'Please review the highlighted fields and try again.';

const getBenefitError = (error: unknown): string => {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: { error?: string } }).data;
    if (data?.error) return data.error;
  }
  return 'We could not reserve this job credit. Please review your selection and try again.';
};

interface IJobStepperProps {
  isComplete?: boolean;
  leftInfoPanel?: ReactNode;
  finalComponent?: ReactNode;
  isStepperLoading?: boolean;
  finalStepperRequest: (val: { body: any; id: string; validateOnly: boolean; isFree?: boolean }) => Promise<unknown>
}

export const JobStepper = ({ isComplete, isStepperLoading, finalStepperRequest, leftInfoPanel, finalComponent }:IJobStepperProps) => {
  const dispatch = useDispatch();
  const [isGetTempData, setIsGetTempData] = useState(true);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [stepperIsDropped, setStepperIsDropped] = useState<boolean>(false);

  const { isStepperDrop } = useAppSelector(({ stepperOptions }) => ({
    isStepperDrop: stepperOptions.isDrop
  }));

  const [saveStepsData, { data: savedData, error: errSavedData }] = useSaveStepsDataMutation();
  const [paymentRequest] = usePaymentRequestMutation();
  const [freePaymentRequest] = useFreePaymentRequestMutation();
  const [isFirstLoadPage, setIsFirstLoadPage] = useState(true);

  // siteId is driven solely by the route brand (slicePathname): /bevnet→1,
  // /brewbound→2, /nosh→3, bare→CPG(8). This previously fell back to the browser-host
  // id (jobsContent.hostId) for the non-/brewbound|/nosh routes, which on
  // app.cpgjobs.com resolves to BevNET(1); once the bare route maps to CPG(8) that
  // would mismatch the finalize siteId (also slicePathname) and fail the backend
  // amount-integrity check. Keying intent + validate + finalize all off slicePathname
  // keeps them in sync (D-24371).
  const { siteNumber } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber
  }));
  const siteId = siteNumber.toString();

  const [reachedPayment, setReachedPayment] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [getPaymentIntent] = useGetPaymentIntentMutation();
  const [benefitCheckout] = useBenefitCheckoutMutation();
  const [releaseBenefitCheckout] = useReleaseBenefitCheckoutMutation();
  const [benefitSelection, setBenefitSelection] = useState<IJobBenefitSelection | null>(null);
  const [benefitClaim, setBenefitClaim] = useState<IJobBenefitClaim | null>(null);
  const benefitClaimRef = useRef<IJobBenefitClaim | null>(null);
  const benefitCheckoutQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [benefitCheckoutError, setBenefitCheckoutError] = useState<string | null>(null);

  const [isInternalLoading, setIsInternalLoading] = useState<boolean>(false);

  const { data: getSavedTempData, isLoading: getTempDataLoading, } = useGetJobTempDataQuery(cookies.get(cookieNames.tempJob) || '', {
    skip: isGetTempData
  });

  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons, dropStepper, goToStep } = useStepper({
    orientation: 'vertical',
    leftInfoPanel,
    styleProps: { widthAllStepper: 56.25 },
    finalComponent: finalComponent || (<></>),
    isLoading: getTempDataLoading || isStepperDrop || isInternalLoading || isStepperLoading
  });

  //  steps data (lazy initializers — every mount/reset gets its own object graph,
  //  so no two step states ever share a nested `locations` / `tempAdditionalEmails`)
  const [step1, setStep1] = useState<IStep1Form>(makeStaticValuesForStep1);
  const [step2, setStep2] = useState<IStep2Form>(makeStaticValuesForStep2);
  const [step3, setStep3] = useState<IStep3Form>(makeStaticValueForStep3);
  const [step4, setStep4] = useState<IStep4Form>(makeStaticValuesForStep4);
  const [step5, setStep5] = useState<IStep5Form>(makeStaticValuesForStep5);
  const [step6, setStep6] = useState<IStep6Form>(makeStaticValuesForStep6);
  const [step7] = useState<IStep7Form>(makeStaticValuesForStep7);

  const [jobListingTypes, setJobListingTypes] = useState<JobListingTypesResponse>(null);

  // Server validation errors, keyed by the UI step that owns them. They must live
  // here rather than on the steps: StepContent is unmountOnExit, so at submit time
  // (from Step 7) every other step's form instance — and its setError — is gone.
  // Each step re-applies its own slice when it mounts.
  const [serverErrors, setServerErrors] = useState<StepServerErrors | null>(null);

  // Prefill the poster's Step 6 contact fields from the logged-in user. Already
  // fetched app-wide (App.tsx), so this reads the RTK Query cache.
  const { data: currentUserInfo } = useGetUserInfoQuery();
  const contactPrefilled = useRef(false);

  // Navigation gate for serverErrors. True only when a failed save just populated the
  // slice map — the one case that should jump to lowestStep. dropStepSlice-driven
  // clears (a step re-validating) leave it false, so clearing a validated step's stale
  // error never moves the user (D-24094 #20).
  const pendingServerNavRef = useRef(false);

  const createIntent = useCallback(async () => {
    setIntentError(null);
    // Invariant guard: refuse to POST a PaymentIntent with an out-of-range listing
    // type. JSON.stringify drops undefined keys, so an unset listingType would omit
    // it from the body and the server would bind the enum default 0 → hard fail
    // (D-32/D-33). The restore now coerces listingType to 1|2, so this should never
    // fire; it surfaces an error instead of sending a known-bad request rather than
    // silently coercing (which could charge the wrong tier). 1=Standard, 2=Featured.
    if (step1.listingType !== 1 && step1.listingType !== 2) {
      setClientSecret(null);
      setIntentError('We could not start payment. Please reload the page and try again.');
      return;
    }
    const result = await getPaymentIntent(buildPostAJobIntentBody({
      listingType: step1.listingType,
      siteId,
      standardPrice: jobListingTypes?.standardPrice,
      featuredPrice: jobListingTypes?.featuredPrice,
      couponCode: step1.couponCode
    }));
    if ('data' in result) {
      const outcome = interpretIntentResponse(result.data);
      setClientSecret(outcome.clientSecret);
      setIntentError(outcome.error);
    } else {
      setClientSecret(null);
      setIntentError(GENERIC_INTENT_ERROR);
    }
    // depend on primitive pricing inputs only (jobListingTypes object identity is
    // unstable — using it directly would re-fire the intent every render).
  }, [getPaymentIntent, step1.listingType, step1.couponCode, siteId, jobListingTypes?.standardPrice, jobListingTypes?.featuredPrice]);

  // The claim GUID the server issued is the only authority over a reservation, and this
  // page can be reloaded after one exists. Prefer the live claim, then the one persisted
  // for this draft — without it a reloaded checkout collides with its own reservation.
  const heldClaim = useCallback((): IHeldBenefitClaim | null => {
    const claim = benefitClaimRef.current;
    if (!claim) return readHeldBenefitClaim();
    return {
      draftGuid: claim.draftGuid || '',
      claimCheckoutGuid: claim.checkout.claimCheckoutGuid,
      contactEmail: claim.contactEmail,
    };
  }, []);

  const forgetBenefitClaim = useCallback(() => {
    benefitClaimRef.current = null;
    setBenefitClaim(null);
    clearHeldBenefitClaim();
  }, []);

  const prepareCheckoutNow = useCallback(async () => {
    setBenefitCheckoutError(null);
    setIntentError(null);

    if (!benefitSelection) {
      const existingClaim = heldClaim();
      if (existingClaim) {
        const releaseResult = await releaseBenefitCheckout({
          claimCheckoutGuid: existingClaim.claimCheckoutGuid,
          ...(existingClaim.contactEmail ? { contactEmail: existingClaim.contactEmail } : {})
        });
        if ('error' in releaseResult) {
          const message = getBenefitError(releaseResult.error);
          setClientSecret(null);
          setBenefitClaim(null);
          setBenefitCheckoutError(message);
          return;
        }
        forgetBenefitClaim();
      }
      await createIntent();
      return;
    }

    const { option, organizationId } = benefitSelection;
    const draftGuid = cookies.get(cookieNames.tempJob) || undefined;
    const previousClaim = benefitClaimRef.current;
    const existingClaim = heldClaim();
    const checkoutResult = await benefitCheckout(buildBenefitCheckoutBody({
      siteId,
      listingType: step1.listingType,
      organizationId,
      couponCode: step1.couponCode,
      contactEmail: step6.email,
      option,
      draftGuid,
      existingClaimCheckoutGuid: existingClaim?.claimCheckoutGuid,
      existingClaimContactEmail: existingClaim?.contactEmail,
    }));

    if ('error' in checkoutResult) {
      const message = getBenefitError(checkoutResult.error);
      setClientSecret(null);
      setBenefitClaim(previousClaim);
      setBenefitCheckoutError(message);
      return;
    }

    if (checkoutResult.data.finalAmount > 0 && !checkoutResult.data.clientSecret) {
      await releaseBenefitCheckout({
        claimCheckoutGuid: checkoutResult.data.claimCheckoutGuid,
        ...(step6.email ? { contactEmail: step6.email } : {})
      });
      setClientSecret(null);
      forgetBenefitClaim();
      setBenefitCheckoutError('We could not start payment for the remaining balance. Please try again.');
      return;
    }

    const claim = {
      organizationId,
      draftGuid,
      contactEmail: step6.email || undefined,
      quote: option,
      checkout: checkoutResult.data,
    };
    benefitClaimRef.current = claim;
    setBenefitClaim(claim);
    if (draftGuid) {
      storeHeldBenefitClaim({
        draftGuid,
        claimCheckoutGuid: checkoutResult.data.claimCheckoutGuid,
        contactEmail: claim.contactEmail,
      });
    }
    setClientSecret(checkoutResult.data.clientSecret || null);
  }, [benefitSelection, benefitCheckout, releaseBenefitCheckout, createIntent, heldClaim,
    forgetBenefitClaim, siteId, step1.listingType, step1.couponCode, step6.email]);

  // Serialize checkout changes. Each queued operation reads benefitClaimRef only
  // when it starts, so rapid A→B→C edits atomically swap B from the claim returned
  // by A, then C from B. Parallel requests would all carry the same stale claim
  // GUID and could strand the latest selection behind the active-draft constraint.
  const prepareCheckout = useCallback(() => {
    const pending = benefitCheckoutQueueRef.current.then(prepareCheckoutNow, prepareCheckoutNow);
    benefitCheckoutQueueRef.current = pending.then(() => undefined, () => undefined);
    return pending;
  }, [prepareCheckoutNow]);

  // Create the PaymentIntent when the payment step is first reached, and recreate
  // it whenever pricing inputs change afterwards (back-nav edits, marker jumps).
  // The reachedPayment gate is still required, but NOT for the reason this comment
  // used to give: MUI's StepContent is unmountOnExit (StepContent.js:130 in the
  // resolved @mui/material 5.10.12), so step bodies are destroyed on leave, not kept
  // mounted. This effect lives in the container, which IS mounted from page load —
  // that is what would fire the intent immediately without the gate.
  useEffect(() => {
    if (reachedPayment) {
      prepareCheckout();
    }
  }, [reachedPayment, prepareCheckout]);

  const finalPaymentRequest = (val: IStep7Form, validateOnly: boolean) => {
    const draftGuid = cookies.get(cookieNames.tempJob) || undefined;
    return finalStepperRequest({
      id: siteId,
      validateOnly,
      body: {
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7: val,
        // Last-touch UTM attribution: read the shared `cpg_utm` cookie at conversion
        // and send it as a top-level sibling of the steps to POST /Jobs/PostJobListing.
        // undefined (no UTM captured) is dropped by JSON serialization → backend null.
        utmParams: readUtmCookie(),
        ...(benefitClaim?.organizationId ? { organizationId: benefitClaim.organizationId } : {}),
        ...(draftGuid ? { draftGuid } : {}),
        ...(benefitClaim?.checkout.claimCheckoutGuid
          ? { claimCheckoutGuid: benefitClaim.checkout.claimCheckoutGuid }
          : {}),
      }
    });
  };

  const interpretValidationResponse = (saveDataResponse: any): { success: boolean; message: string } => {
    if (saveDataResponse && 'error' in saveDataResponse) {
      const domainMessage = getResponseDomainError(saveDataResponse.error);
      if (domainMessage) {
        // Domain claim failures use { error }, not FluentValidation's { errors }.
        // The server has either released an unusable claim or denied access to it;
        // discard the stale client authority — persisted as well as in memory, or a
        // reload would keep resubmitting it — and make retry obtain a fresh quote.
        if (benefitClaimRef.current || readHeldBenefitClaim()) {
          setClientSecret(null);
          forgetBenefitClaim();
          setBenefitCheckoutError(domainMessage);
        }
        return { success: false, message: domainMessage };
      }
      // The old parser did Object.values(error.data.errors) — it discarded the keys
      // (so every message piled up on Step 7 as one blob) and threw outright on any
      // response without an `errors` map, turning a 500 into a client crash.
      const parsed = parseServerStepErrors(
        getResponseErrors(saveDataResponse.error),
        POST_JOB_STEP_INDEX
      );

      if (!parsed.hasErrors) {
        return { success: false, message: GENERIC_SERVER_ERROR };
      }

      // Storing the errors is what triggers navigation (see the effect below), so
      // the step is always mounted with its errors already in hand. A dotless
      // `StepN` blames a step without naming a field, hence lowestStep — not the
      // presence of field errors — decides whether we move.
      if (parsed.lowestStep !== null) {
        // Only a fresh save-failure should navigate; the slice-clears wired into each
        // step's onNext must not (they leave pendingServerNavRef false).
        pendingServerNavRef.current = true;
        setServerErrors({ byStep: parsed.fieldErrorsByStep, lowestStep: parsed.lowestStep });
      }

      // Anything no field can render — step-level NotNull messages, Step 7's own
      // billing rules, unprefixed keys — still has to reach the user.
      const message = parsed.unroutedMessages.length
        ? parsed.unroutedMessages.join('\n')
        : STEP_ERRORS_NOTICE;

      return { success: false, message };
    }

    return { success: true, message: '' };
  };

  const validateFormData = async (val: IStep7Form, validateOnly: boolean): Promise<{ success: boolean, message: string }> => {
    const draftGuid = cookies.get(cookieNames.tempJob) || undefined;
    const saveDataResponse = await paymentRequest({
      id: siteId,
      validateOnly,
      body: {
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7: val,
        ...(benefitClaim?.organizationId ? { organizationId: benefitClaim.organizationId } : {}),
        ...(draftGuid ? { draftGuid } : {}),
        ...(benefitClaim?.checkout.claimCheckoutGuid
          ? { claimCheckoutGuid: benefitClaim.checkout.claimCheckoutGuid }
          : {}),
      }
    });
    return interpretValidationResponse(saveDataResponse);
  };

  const freeRequestBody = () => ({
    step1,
    step2,
    step3,
    step4,
    step5,
    step6,
    ...(benefitClaim?.organizationId ? { organizationId: benefitClaim.organizationId } : {}),
    ...(benefitClaim?.draftGuid ? { draftGuid: benefitClaim.draftGuid } : {}),
    claimCheckoutGuid: benefitClaim?.checkout.claimCheckoutGuid || '',
    utmParams: readUtmCookie(),
  });

  const validateFreeFormData = async (): Promise<{ success: boolean, message: string }> => {
    const saveDataResponse = await freePaymentRequest({
      id: siteId,
      validateOnly: true,
      body: freeRequestBody()
    });
    return interpretValidationResponse(saveDataResponse);
  };

  const finalFreeRequest = () => finalStepperRequest({
    id: siteId,
    validateOnly: false,
    isFree: true,
    body: freeRequestBody(),
  });

  const steps = useMemo(() => [
    {
      label: 'Listing Type',
      content: (
        <Step1
          setIsInternalLoading={setIsInternalLoading}
          setIsRequest={setIsRequest}
          setJobListing={setStep1}
          Nav={StepsButtons}
          defaultData={step1}
          onNext={onNext}
          setJobListingTypes={setJobListingTypes}
          setBenefitSelection={setBenefitSelection}
        />
      )
    },
    {
      label: 'Basic Job Details',
      content: (
        <Step2
          Nav={StepsButtons}
          defaultData={step2}
          setStep2={setStep2}
          onBack={onBack}
          onNext={() => {
            setServerErrors((prev) => dropStepSlice(prev, 1));
            onNext();
          }}
          serverErrors={serverErrors?.byStep[1]}
        />
      )
    },
    {
      label: 'Compensation & Location',
      content: (
        <Step3
          Nav={StepsButtons}
          defaultData={step3}
          setStep3={setStep3}
          onBack={onBack}
          onNext={() => {
            setServerErrors((prev) => dropStepSlice(prev, 2));
            onNext();
          }}
          serverErrors={serverErrors?.byStep[2]}
        />
      )
    },
    {
      label: 'Job Description',
      content: (
        <Step4
          Nav={StepsButtons}
          defaultData={step4}
          setStep4={setStep4}
          onBack={onBack}
          onNext={() => {
            setServerErrors((prev) => dropStepSlice(prev, 3));
            onNext();
          }}
          serverErrors={serverErrors?.byStep[3]}
        />
      )
    },
    {
      label: 'Upload Logo',
      content: (
        <Step5
          Nav={StepsButtons}
          defaultData={step5}
          setStep5={setStep5}
          linkedSupplierId={step2.supplierId}
          onBack={onBack}
          onNext={onNext}
        />
      )
    },
    {
      label: 'Contact and Application Details',
      content: (
        <Step6
          onNext={() => {
            setReachedPayment(true);
            setServerErrors((prev) => dropStepSlice(prev, 5));
            onNext();
          }}
          onBack={onBack}
          Nav={StepsButtons}
          defaultValue={step6}
          setStep6={setStep6}
          serverErrors={serverErrors?.byStep[5]}
        />
      )
    },
    {
      label: benefitClaim?.checkout.finalAmount === 0 ? 'Review & Submit' : 'Payment Information',
      content: (
        <Step7
          onBack={onBack}
          Nav={StepsButtons}
          defaultData={step7}
          onNext={onComplete}
          listingType={step1.listingType}
          validateFormData={validateFormData}
          finalPaymentRequest={finalPaymentRequest}
          setIsInternalLoading={setIsInternalLoading}
          standardPrice={jobListingTypes?.standardPrice}
          featuredPrice={jobListingTypes?.featuredPrice}
          clientSecret={clientSecret}
          intentError={benefitCheckoutError || intentError}
          retryIntent={prepareCheckout}
          benefitClaim={benefitClaim}
          validateFreeFormData={validateFreeFormData}
          finalFreeRequest={finalFreeRequest}
        />
      )
    }
  ], [StepsButtons, step1, step2, step3, step4, step5, step6, step7, clientSecret, intentError, benefitCheckoutError, prepareCheckout, benefitClaim, serverErrors]);

  useEffect(() => {
    dispatch(showDropButton());
    putSteps(steps);
    if (!isFirstLoadPage && isRequest) {
      saveStepsData({
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7,
      });
    }
  }, [step1, step2, step3, step4, step5, step6, step7, isRequest]);

  // Re-publish steps when checkout state or routed server errors change, so Step7
  // receives the fresh claim/clientSecret/error and each step receives its errors
  // (useStepper renders a putSteps snapshot, not live children — without this the
  // steps keep their old props and no error ever appears).
  // Kept separate from the save effect above so neither re-saves temp data.
  useEffect(() => {
    putSteps(steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, intentError, benefitCheckoutError, benefitClaim, prepareCheckout, serverErrors]);

  // Navigate to the lowest step the server blamed. Driven off the stored errors
  // rather than called from validateFormData directly, for two reasons: it makes
  // "store, then navigate" true by construction, and validateFormData is captured in
  // the putSteps snapshot — a goToStep closure inside it would carry a stale
  // numbersFilledStep and could silently refuse to move.
  useEffect(() => {
    // Navigate only when the errors came from a fresh save-failure. Clearing a
    // validated step's slice also changes `serverErrors`, but must not navigate —
    // pendingServerNavRef stays false on that path.
    if (serverErrors && pendingServerNavRef.current) {
      pendingServerNavRef.current = false;
      goToStep(serverErrors.lowestStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverErrors]);

  useEffect(() => {
    if (isFirstLoadPage && !cookies.get(cookieNames.tempJob)) {
      setIsFirstLoadPage(false);
    }
    if (isFirstLoadPage && cookies.get(cookieNames.tempJob)) {
      setIsGetTempData(false);
    }
  }, []);
  //
  useEffect(() => {
    if (savedData && savedData.guid && !cookies.get(cookieNames.tempJob)) {
      cookies.set(cookieNames.tempJob, savedData.guid);
      dispatch(showDropButton());
    }
  }, [savedData, errSavedData]);

  useEffect(() => {
    if (isStepperDrop === false && cookies.get(cookieNames.tempJob)) {
      if (isFirstLoadPage && getSavedTempData) {
        // Temp-data round-trip can return step1 without a valid listingType (the
        // server read model drops it / yields 0). Never let it reach the PI request
        // as undefined — fall back to the Standard default so the Step 1 radio
        // selection and the charged tier stay consistent. (Root cause of D-33.)
        setStep1({
          ...getSavedTempData.step1,
          listingType: getSavedTempData.step1?.listingType || staticValuesForStep1.listingType,
        });
        setStep2(getSavedTempData.step2);
        setStep3(getSavedTempData.step3);
        setStep4(getSavedTempData.step4);
        setStep5(getSavedTempData.step5);
        setStep6(getSavedTempData.step6);
        setIsFirstLoadPage(false);
      }
    }
  }, [getSavedTempData, isStepperDrop]);

  // Seed Step 6 contact fields from the logged-in user, once. Runs only after the
  // first-load path settles (isFirstLoadPage === false) so it never races the
  // saved-draft restore above; skips anonymous sessions.
  //
  // There is deliberately no `tempJob` cookie check here. Skipping the whole prefill
  // whenever a draft existed is what produced §15's locked-and-empty Step 6, and it
  // was redundant anyway: `prev.field ||` below already lets a restored draft win
  // field by field, which also fills the gaps in a PARTIAL draft rather than
  // abandoning every field because one of them was saved.
  useEffect(() => {
    if (contactPrefilled.current) return;
    if (isFirstLoadPage) return;
    if (!currentUserInfo?.isLoggedIn) return;
    contactPrefilled.current = true;
    setStep6((prev) => ({
      ...prev,
      contactFirstName: prev.contactFirstName || currentUserInfo.firstName || '',
      contactLastName: prev.contactLastName || currentUserInfo.lastName || '',
      email: prev.email || currentUserInfo.email || '',
    }));
  }, [currentUserInfo, isFirstLoadPage]);

  useEffect(() => {
    if (isComplete && !stepperIsDropped) {
      // The listing is posted: nothing is left to lose, so the leave-page guard is off
      // until the next listing is started.
      setIsRequest(false);
      setStepperIsDropped(true);
      onComplete();
      const timeout = setTimeout(() => {
        setStepperIsDropped(false);
        clearTimeout(timeout);
      }, 3000);
    }
  }, [isComplete]);

  // "Start over". Resets go through factories: re-assigning the shared module
  // constant is an Object.is no-op, so React bails, no dependency of the publish
  // effect above changes, and `putSteps` never re-fires after `dropStepper()`
  // emptied the steps — which leaves the stepper blank for good.
  //
  // On THIS flow that is defensive, not a live fix, and it is worth knowing why:
  // `dropStepper()` also resets activeStep to 0, which remounts Step1, whose own
  // effect (steps/Step1/index.tsx) calls setJobListing with a fresh object every
  // mount. That changes step1's identity for us and re-fires the publish effect,
  // so the wipe never actually surfaces here. Verified by instrumentation, not
  // assumed. It is an accident of an unrelated component, so don't lean on it.
  // The same reset on PostPrepaidListing genuinely wipes the stepper (its first
  // step takes no setter, so nothing re-seeds) — that is D-24094 §13.
  useEffect(() => {
    dispatch(showDropButton());
    if (isStepperDrop) {
      const resetAfterReleasingBenefit = async () => {
        await benefitCheckoutQueueRef.current;
        const existingClaim = heldClaim();
        if (existingClaim) {
          const releaseResult = await releaseBenefitCheckout({
            claimCheckoutGuid: existingClaim.claimCheckoutGuid,
            ...(existingClaim.contactEmail ? { contactEmail: existingClaim.contactEmail } : {})
          });
          if ('error' in releaseResult) {
            const message = getBenefitError(releaseResult.error);
            setBenefitCheckoutError(message);
            dispatch(showNotification({ show: true, type: 'error', text: message }));
            return;
          }
        }

        setIsRequest(false);
        setStep1(makeStaticValuesForStep1());
        setStep2(makeStaticValuesForStep2());
        setStep3(makeStaticValueForStep3());
        setStep4(makeStaticValuesForStep4());
        setStep6(makeStaticValuesForStep6());
        setStep5(makeStaticValuesForStep5());
        forgetBenefitClaim();
        setBenefitSelection(null);
        setClientSecret(null);
        setBenefitCheckoutError(null);
        dispatch(showNotification({
          show: true,
          type: 'info',
          text: 'Form cleared'
        }));
        dropStepper();
      };

      void resetAfterReleasingBenefit();
    }
  }, [isStepperDrop, releaseBenefitCheckout, heldClaim, forgetBenefitClaim, dispatch, dropStepper]);

  return (
    <>
      {/* Armed from the first Next on Step 1 until the job is posted or "Start over". */}
      <LeavePageGuard
        when={isRequest}
        message={UNPOSTED_LISTING_MESSAGE}
      />
      {renderStepper}
    </>
  );
};
